"use client";

import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import MuiLink from "@mui/material/Link";
import Link from "next/link";
import { useState } from "react";
import { LoadingButton } from "@mui/lab";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useRouter } from "@bprogress/next";
import Flexbox from "../components/Flexbox";
import { useUserUpdaterContext } from "../providers/UserProvider";
import { useAppConfigsUpdater } from "../providers/AppConfigProvider";
import { useAuthModalStateUpdater } from "../providers/AuthModal";
import PlainLoadingButton from "./PlainLoadingButton";
import GoogleIcon from "../icons/google-icon";
import TextDivider from "./TextDivider";

const LoginComponent = ({
  showSnackbar,
  redirectOnSuccess = false,
  redirectURL = null,
}) => {
  const updateUser = useUserUpdaterContext();
  const configUpdater = useAppConfigsUpdater();
  const setAuthModalState = useAuthModalStateUpdater();
  const router = useRouter();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const togglePassword = () => {
    setShowPassword((x) => !x);
  };

  const closeModal = () => {
    setAuthModalState((x) => ({ ...x, open: false }));
  };

  const handleFieldChange = (t) => (e) => {
    setFormData((x) => {
      return { ...x, [t]: e.target.value };
    });
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    try {
      setIsProcessing(true);
      const res = await fetch(
        process.env.NEXT_PUBLIC_REQUEST_HOST + "/api/auth/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
          }),
          credentials: "include",
          next: { revalidate: 0 },
        }
      );

      if (res.ok) {
        const data = await res.json();
        updateUser(data.user);
        const userConfigs = data.user.preferences;

        configUpdater((p) => {
          return {
            ...p,
            fontSize: userConfigs?.font_size ?? 14,
            theme:
              (userConfigs?.theme ?? "dark") +
              "-" +
              (userConfigs?.accent_color ?? "default"),
          };
        });

        showSnackbar("success", "Login successfull");
        setIsProcessing(false);

        if (redirectOnSuccess) {
          router.replace(redirectURL);
        } else {
          setAuthModalState((x) => ({ ...x, open: false }));
        }
      } else {
        const data = await res.json();
        showSnackbar("error", data.message);
        setIsProcessing(false);
      }
    } catch (e) {
      showSnackbar("error", e?.message || "Something went wrong");
      setIsProcessing(false);
    }
  };

  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  const loginUsingGoogle = async (e) => {
    try {
      e.preventDefault();
      setIsGeneratingLink(true);
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_REQUEST_HOST}/api/oauth/geturl`,
        {
          credentials: "include",
        }
      );
      if (typeof window !== "undefined") {
        if (res.ok) {
          const data = await res.json();
          window.location.href = data.url;
          setIsGeneratingLink(false);
        } else {
          throw Error("Unable to generate link");
        }
      }
    } catch (e) {
      setIsGeneratingLink(false);
      showSnackbar("error", e?.message ?? "Something went wrong");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1.5rem",
        flexDirection: "column",
      }}
    >
      <PlainLoadingButton
        startIcon={isGeneratingLink ? null : <GoogleIcon />}
        onClick={loginUsingGoogle}
        disabled={isGeneratingLink || isProcessing}
        loading={isGeneratingLink}
        variant="outlined"
        sx={{
          textTransform: "none",
          borderRadius: "100px",
          fontSize: "1rem",
        }}
      >
        Sign in with Google
      </PlainLoadingButton>
      <TextDivider my="0" text={"or sign in with email"} />

      <Box
        component={"form"}
        id="login-form"
        sx={{
          maxWidth: "400px",
          display: "flex",
          gap: "1.5rem",
          flexDirection: "column",
        }}
        onSubmit={handleLogin}
      >
        <TextField
          disabled={isGeneratingLink || isProcessing}
          id="login-email"
          label="Email"
          value={formData.email}
          type="email"
          required
          autoComplete="username"
          fullWidth
          onChange={handleFieldChange("email")}
        />
        <TextField
          disabled={isGeneratingLink || isProcessing}
          id="login-password"
          autoComplete="current-password"
          value={formData.password}
          label="Password"
          type={showPassword ? "text" : "password"}
          required
          fullWidth
          onChange={handleFieldChange("password")}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={togglePassword}
                  disabled={isGeneratingLink || isProcessing}
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            justifyContent: "space-between",
          }}
        >
          <Box></Box>
          <MuiLink
            fontSize={"small"}
            component={Link}
            href="/forgot-password"
            onClick={closeModal}
          >
            Forgot password ?
          </MuiLink>
        </Box>
        <Flexbox
          sx={{
            gap: "1rem",
          }}
        >
          <LoadingButton
            disabled={isGeneratingLink || isProcessing}
            type="submit"
            variant="contained"
            loading={isProcessing}
            sx={{
              flexGrow: 1,
              borderRadius: "100px",
              fontSize: "1rem",
              textTransform: "none",
            }}
          >
            Sign in
          </LoadingButton>
        </Flexbox>
      </Box>
    </Box>
  );
};

export default LoginComponent;
